import { useCallback } from "react"
import { useToast } from "@hooks"
import { strategiesApi } from "Pages/DsRepricing/Page/@services/api"
import { fromServer, toServer } from "Pages/DsRepricing/Page/@services/adapters"
import {
	dsRepricingIsSubmitting,
	dsRepricingListData,
	clearDsRepricingSignals,
} from "Pages/DsRepricing/Page/@signals"
import { DEFAULT_PRIORITY } from "Pages/DsRepricing/Page/@components/Priority"
import { readPeriod } from "Pages/DsRepricing/Page/@components/periodShape"

const extractId = o => (typeof o === 'object' && o !== null) ? o.value : o
const extractIds = arr => (arr ?? []).map(extractId)

const num = v => v !== '' && v != null ? Number(v) : null

// Only the fields the strategy actually uses are sent: the backend rejects a market_trend
// carrying a targetPosition and vice versa.
const normalizeStrategy = (strategy) => {
	if (!strategy?.type) return strategy
	if (strategy.type === 'ranking') {
		return {
			type: 'ranking',
			targetPosition: num(strategy.targetPosition),
			keepPosition: !!strategy.keepPosition,
			...(strategy.keepPosition
				? { keepPositionMargin: strategy.keepPositionMargin ?? null }
				: {}),
		}
	}
	return {
		type: 'market_trend',
		rankCap: num(strategy.rankCap),
		excludeOutliersPct: num(strategy.excludeOutliersPct),
	}
}

// Advanced.kpi_conditions is null unless there is at least one fully
// filled condition. Half-typed conditions (kpi picked but no value)
// are dropped so the backend never receives a NaN gate.
const normalizeAdvanced = (adv) => {
	const list = adv?.kpi_conditions ?? []
	const valid = list.filter(c => c && c.kpi && c.op &&
	                              c.value != null && c.value !== '' && Number.isFinite(Number(c.value)))
		.map(c => ({ kpi: c.kpi, op: c.op, value: Number(c.value) }))
	return valid.length === 0 ? null : { kpi_conditions: valid }
}

// The ladder groups (smartAcrissGroups / smartCategoryGroups) used to live
// inside the strategy but now belong to Setup → Category Consistency; the
// only thing the strategy still owns is the boolean flag that turns the
// smart-pricing algorithm on for this rule.
const normalizeTools = (tools) => {
	if (!tools?.smartAcrissPricing) return null
	return { smartAcrissPricing: true }
}

// One object with one key per mode, and only one of them ever filled — the mode toggle empties the
// other two. The backend rejects a period carrying more than one, or none.
const normalizePeriod = (data) => {
	const period = readPeriod(data)
	return {
		seasons: period.seasons,
		leadTimes: period.leadTimes.map(({ leadFrom, leadTo }) => ({
			leadFrom: num(leadFrom), leadTo: num(leadTo),
		})),
		weekdays: period.weekdays.map(num).filter(day => day != null),
	}
}

// Read-only/derived fields that arrive with a loaded rule (or with a clone) and must not be sent
// back: `uid`, `enabled` and `show` are owned by the backend — `_set_user` honours an incoming uid
// and `update` writes whatever it is given, so echoing them lets the client decide who owns the
// rule and whether it is active. The rest are display-only — `product` and `sitesStale` are the
// server's verdicts for the table (CU-869fhuhy6); `_openStep` / `_openCard` are the drawer's own.
const STRIP_ON_SUBMIT = ['uid', 'enabled', 'show', 'reportsName', 'timestampSorted', 'timestamp_create', 'timestamp_update',
                         'product', 'sitesStale', '_openStep', '_openCard']

const withoutReadOnly = data => Object.fromEntries(
	Object.entries(data ?? {}).filter(([key]) => !STRIP_ON_SUBMIT.includes(key))
)

// Exported for `useRateShops`: the wizard's draft travels to the engine's
// estimate through the same two steps as a save (CU-869fhuhy6, § 5.2).
export const transformData = (data) => ({
	...withoutReadOnly(data),
	filterGroupOffices: extractIds(data.filterGroupOffices),
	filterGroupSites: extractIds(data.filterGroupSites),
	filterGroupWebSuppliers: extractIds(data.filterGroupWebSuppliers),
	filterGroupProviders: extractIds(data.filterGroupProviders),
	filterProviders: extractIds(data.filterProviders),
	filterAcriss: extractIds(data.filterAcriss),
	filterCategories: extractIds(data.filterCategories),
	filterCategoriesExclusion: data.filterCategoriesExclusion ?? false,
	// The product, whom it is compared against and when it runs
	// (CU-869fhuhy6) travel as the drawer holds them; `toServer` shapes
	// the three for the wire. No face axes since brief v3: the product
	// says which of our rows the rule prices.
	productId: data.productId ?? null,
	compareAgainst: data.compareAgainst ?? null,
	whenItRuns: data.whenItRuns ?? null,
	compareByCategory: data.compareByCategory ?? false,
	strategy: normalizeStrategy(data.strategy),
	// A single offset for the whole rule: per-provider overrides were dropped from the tool, so
	// whatever a rule saved before that still carries is not sent back.
	undercut: data.undercut ? {
		value: data.undercut.value,
		type: data.undercut.type,
		direction: data.undercut.direction ?? 0,
	} : data.undercut,
	// No `marketDeviationPct`: the "Unless off market by" field was dropped from the form, so echoing
	// back whatever an old rule stored would keep a guard alive that nobody can see or change.
	competitorRule: data.competitorRule ? {
		neverBelow: extractIds(data.competitorRule.neverBelow),
		neverAbove: extractIds(data.competitorRule.neverAbove),
	} : null,
	// Tethering rules live in Setup as a shared library; the strategy
	// only carries the ids it wants applied. The engine looks them up
	// server-side by id.
	tethers: Array.isArray(data.tethers) ? data.tethers.filter(Boolean) : [],
	// Advanced mode payload — KPI-based activation gate. Empty or absent
	// means "no gate", so we normalize to `null` (never send `{ kpi_conditions: [] }`
	// which would look like an intentional empty gate).
	advanced: normalizeAdvanced(data.advanced),
	period: normalizePeriod(data),
	priority: num(data.priority) ?? DEFAULT_PRIORITY,
	tools: normalizeTools(data.tools),
	// The following fields moved to Setup and are no longer strategy-owned:
	//   • tethering       → Setup → Tethering
	//   • minMax          → Setup → Price Limits
	//   • maxMarkup       → Setup → Products (markup cap per site row)
	//   • tools.smartAcrissGroups / smartCategoryGroups → Setup → Category Consistency
	// Legacy rules that still carry any of these are stripped here so the
	// backend never sees them again from this client. `withoutReadOnly`
	// is not enough because these keys are not on STRIP_ON_SUBMIT — kept
	// separate so the intent (setup migration, not read-only) stays clear.
	tethering: undefined, minMax: undefined, maxMarkup: undefined,
})

export default function useDsRepricingFormSubmit({ getTableData, onOverlap }) {
	const { showToast } = useToast()

	const save = useCallback((payload, isEdit, id) => {
		const dto = toServer(payload)

		dsRepricingIsSubmitting.value = true

		const request = isEdit
			? strategiesApi.update(id, dto)
			: strategiesApi.create(dto)

		request
			.then(row => {
				const result = fromServer(row)
				dsRepricingIsSubmitting.value = false

				if (isEdit) {
					dsRepricingListData.value = dsRepricingListData.value.map(item =>
						item.id === result.id ? result : item
					)
				} else {
					getTableData()
				}

				clearDsRepricingSignals()
				showToast.success(isEdit ? "Strategy updated" : "Strategy created")
			})
			.catch((err) => {
				dsRepricingIsSubmitting.value = false

				// A blocking overlap is the one failure with no way out of the form:
				// the drawer signals are left untouched so the operator keeps what they
				// were writing while they go and fix the other strategy.
				if (err?.status === 409 && err?.code === 'strategy_overlap' && onOverlap) {
					onOverlap(err.details?.conflicts ?? [], null)
					return
				}

				// Everything else carries the server's own reason — "a strategy with
				// that name already exists", "the forecast occupancy KPI is not
				// available yet" — which is what the operator needs to act on.
				showToast.error(err?.message || "Error saving the strategy")
			})
	}, [getTableData, onOverlap])

	// Overlaps resolved by priority are legal, so they must not be discovered as a save error.
	// The rule is checked first and the hierarchy shown, with the save one click away.
	const onSubmit = useCallback((data) => {
		const isEdit = !!data.id
		const payload = transformData(data)

		dsRepricingIsSubmitting.value = true

		strategiesApi.overlapCheck(toServer(payload), data.id)
			.then(res => {
				const conflicts = res?.conflicts ?? []
				if (conflicts.length === 0) {
					save(payload, isEdit, data.id)
					return
				}
				dsRepricingIsSubmitting.value = false
				const blocking = conflicts.some(c => c.blocking)
				onOverlap(conflicts, blocking ? null : () => save(payload, isEdit, data.id))
			})
			// The check is an early warning, not a gate: create and update run it again
			// server-side, so a failure here must not stop the operator from saving.
			.catch(() => save(payload, isEdit, data.id))
	}, [save, onOverlap])

	return { onSubmit }
}